import { api, auth } from '@/api';
import type { PtwSessionDto } from '@/api/generator';
import { getSignInRedirect } from '@/router/utils';
import { useAccountStore } from '@/stores/accountStore.ts';
import { useDateTimeStore } from '@/stores/dateTimeStore';
import { useLicenseStore } from '@/stores/licenseStore.ts';
import { useSystemStore } from '@/stores/systemStore';
import { defineStore } from 'pinia';
import { ref, toRef } from 'vue';
import { usePermissionsStore } from './permissionsStore';
import { Router } from 'vue-router';
import { routerInstance } from '@/router/router';
import i18next from 'i18next';
import { defaultErrorHandler } from '@/errorHandling';
import { initAppInsights } from '@/telemetry/appInsights';

export interface AuthenticationBroadcastEvent extends Event {
  data: 'signIn' | 'signOut';
}

/**
 * Use this store for authentication related tasks and data
 */
export const useAuthenticationStore = (router: Router = routerInstance) =>
  defineStore('authentication', () => {
    const session = ref<PtwSessionDto>();

    const broadcast = new BroadcastChannel('authentication');

    broadcast.addEventListener('message', async ({ data: event }: AuthenticationBroadcastEvent) => {
      const refreshOnSignIn = event === 'signIn' && !auth.isAuthenticated;
      const refreshOnSignOut = event === 'signOut' && auth.isAuthenticated;

      if (refreshOnSignIn || refreshOnSignOut) {
        window.location.reload();
      }
    });

    async function updateSession() {
      const { result, raw: response } = await api.account.getSession([defaultErrorHandler], {
        redirect: 'error',
      });

      // there is additional information on the session not related
      // to the user, so we need to always set it
      session.value = result;

      useDateTimeStore().setServerTimeFromResponse(response);
    }

    async function startup() {
      await updateSession();
      await auth.checkAuthentication();
      if (auth.isAuthenticated) {
        await usePermissionsStore().fetchMyPermissions();
      }
    }

    auth.on.signIn(async () => {
      await updateSession();

      if (!session.value?.userId) {
        throw new Error('SignIn went wrong, received on.signIn but no session');
      }

      void useSystemStore().fetchSystemData().then(initAppInsights);

      // initialize stores
      await usePermissionsStore().fetchMyPermissions();

      const accountStore = useAccountStore();
      await accountStore.fetchAccountData(session.value.userId, true);

      await useLicenseStore().fetchLicenseData();

      const enterRoute = getSignInRedirect(router.currentRoute.value);

      if (enterRoute) {
        await router.push(enterRoute);
      }

      // `fetchSession` was called with `isSignIn=true` because we need to defer `i18next.changeLanguage` after the routing.
      // Otherwise `i18next.changeLanguage` would trigger a rerender of the SignInView,
      // if we open up the page for the first time and the browser locale doesn't match the user profile locale.
      await i18next.changeLanguage(accountStore.language);

      // Trigger cross-tab login event
      broadcast.postMessage('signIn');
    });

    auth.on.signOut(async () => {
      // Trigger cross-tab logout event
      broadcast.postMessage('signOut');

      // we don't need to do the following actions, because we do a hard reload
      // - fetch new session
      // - fetch token
      // - pinia reset
    });

    return {
      signIn: auth.signIn.bind(auth),
      signOut: auth.signOut.bind(auth),
      checkAuthentication: auth.checkAuthentication.bind(auth),
      isAuthenticated: toRef(() => auth.isAuthenticated),
      middleware: toRef(() => auth.getFetchMiddleware()),
      startup,
      broadcast,
      session,
    };
  })();
